/**
 * Badge helpers for NCCN AI verdicts and guideline results.
 *
 * Renders the "AI: Yes/No" per-criterion verdict and the "AI: Met/Not Met"
 * guideline-level result with met=green / not-met=red / undetermined=gray
 * colour coding.
 * @module nccnVerdictBadge
 */
import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import React from "react";
import { EvaluationResult } from "../../../data-model/nccnGuidelines";
import { VerdictColor, VERDICT_COLORS } from "../utils/verdictColors";

/** Visual tone of a status tag. */
type Tone = "positive" | "negative" | "neutral";

/** Tag background/text colours keyed by result tone. */
const TONE_TAG_COLORS: Record<Tone, VerdictColor> = {
  positive: VERDICT_COLORS.met,
  negative: VERDICT_COLORS.notMet,
  neutral: VERDICT_COLORS.undetermined,
};

const toneFor = (result: EvaluationResult | null | undefined): Tone => {
  if (result === "True") return "positive";
  if (result === "False") return "negative";
  return "neutral";
};

/**
 * Renders the per-criterion "AI: Yes/No" verdict badge as a fixed blue box.
 *
 * @param props.verdict - The item's evaluation result
 * @returns The rendered badge
 */
export const AiVerdictBadge: React.FC<{
  verdict: EvaluationResult | null | undefined;
}> = ({ verdict }) => {
  const label =
    verdict === "True" ? "Yes" : verdict === "False" ? "No" : "Undetermined";
  return (
    <div className="flex h-[38px] w-28 items-center rounded-md border border-[#C5DBFF] bg-[#F3F8FF] px-3">
      <span className="font-['Lato'] text-xs font-bold leading-[18px] text-[#0056D6]">
        AI:
      </span>
      &nbsp;
      <span className="font-['Lato'] text-xs font-normal leading-[18px] text-[#1A1A1A]">
        {label}
      </span>
    </div>
  );
};

/**
 * Renders the guideline-level "AI: Met/Not Met" result badge.
 *
 * @param props.result - The guideline's final evaluation result
 * @returns The rendered badge
 */
export const GuidelineResultBadge: React.FC<{
  result: EvaluationResult | null | undefined;
}> = ({ result }) => {
  const label =
    result === "True"
      ? "Met"
      : result === "False"
        ? "Not Met"
        : "Undetermined - Human Intervention Needed";
  const tagColors = TONE_TAG_COLORS[toneFor(result)];
  return (
    <Tags
      label={`AI : ${label}`}
      backgroundColor={tagColors.background}
      textColor={tagColors.text}
    />
  );
};
